/**
 * The tenant's move-in check asking the listing server (MTR #599 part 3). Her link's token is the
 * only credential; nothing is kept in the browser. Resolves to the server's answer; throws only
 * when no answer came back, in words she can act on.
 */
export function createMoveInApi({ baseUrl, fetchImpl = null }) {
  const base = String(baseUrl ?? '').replace(/\/+$/, '')
  const go = fetchImpl ?? ((...a) => fetch(...a))
  const call = async (token, action, body) => {
    let res
    try {
      res = await go(`${base}/public/move-in/${token}${action ? `/${action}` : ''}`, action
        ? { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body ?? {}) }
        : { method: 'GET' })
    } catch {
      throw new Error('We couldn’t reach Stayoo. Check your connection and try again.')
    }
    const out = await res.json().catch(() => null)
    if (res.status === 429) return { status: 'too_many_requests', says: 'Too many tries at once. Wait a minute and try again.' }
    if (res.status >= 500 || !out) return { status: 'internal_error', says: 'Something went wrong on our side. Please try again.' }
    return out
  }
  const send = async (token, room, blob) => {
    const where = await call(token, 'report-upload', { room, contentType: blob.type })
    if (where.status !== 'ok') return where
    let put
    try {
      put = await go(where.url, { method: 'PUT', headers: { 'content-type': blob.type }, body: blob })
    } catch {
      throw new Error('The photo didn’t send. Check your connection and try again.')
    }
    if (!put.ok) return { status: 'upload_failed', says: 'The photo didn’t send. Please try again.' }
    return { status: 'ok', path: where.path }
  }
  return {
    open: (token) => call(token),
    matches: (token, room) => call(token, 'room', { room, status: 'matches' }),
    /* Here / Missing; or, for an item she was given more than one of, how many she found (design review of #640, 10). */
    item: (token, item, status, found) => call(token, 'item', found === undefined ? { item, status } : { item, found }),
    sign: (token, name) => call(token, 'sign', { name }),
    /* A report: the photo first, if there is one, then the line. */
    async report(token, room, note, blob = null, onPhoto = null) {
      const sent = blob ? await send(token, room, blob) : null
      if (sent && sent.status !== 'ok') return sent
      return call(token, 'report', { room, note, ...(sent ? { path: sent.path } : {}), ...(onPhoto ? { onPhoto } : {}) })
    },
    /* Change a report: a new photo replaces the old, `removePhoto` takes it off, neither keeps it. */
    async editReport(token, room, id, note, { blob = null, removePhoto = false } = {}) {
      const sent = blob ? await send(token, room, blob) : null
      if (sent && sent.status !== 'ok') return sent
      return call(token, 'report-edit', { id, note, ...(sent ? { path: sent.path } : removePhoto ? { path: null } : {}) })
    },
    deleteReport: (token, id) => call(token, 'report-delete', { id }),
    restoreReport: (token, id) => call(token, 'report-restore', { id }),
  }
}
